/**
 * Character-level helpers shared by the Markdown converter and the HTML sanitiser:
 * escaping, character references, backslash escapes, URL normalisation and the URL
 * allowlist.
 */

const HTML_SPECIAL = /[&<>"]/g;
const HTML_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
};

/** Escape text for element content or a double-quoted attribute value. */
export function escapeHtml(text: string): string {
  return text.replace(HTML_SPECIAL, (c) => HTML_ESCAPES[c] ?? c);
}

/*
 * The named references HTML 4 defined (plus `apos`), as name:code point. HTML5 has
 * over two thousand; these are the ones that turn up in real documents. A well-formed
 * reference outside this list is left exactly as typed.
 */
const ENTITY_SOURCE =
  "quot:34 amp:38 apos:39 lt:60 gt:62 nbsp:160 iexcl:161 cent:162 pound:163 curren:164 yen:165 brvbar:166 sect:167 uml:168 copy:169 ordf:170 laquo:171 not:172 shy:173 reg:174 macr:175 deg:176 plusmn:177 sup2:178 sup3:179 acute:180 micro:181 para:182 middot:183 cedil:184 sup1:185 ordm:186 raquo:187 frac14:188 frac12:189 frac34:190 iquest:191 Agrave:192 Aacute:193 Acirc:194 Atilde:195 Auml:196 Aring:197 AElig:198 Ccedil:199 Egrave:200 Eacute:201 Ecirc:202 Euml:203 Igrave:204 Iacute:205 Icirc:206 Iuml:207 ETH:208 Ntilde:209 Ograve:210 Oacute:211 Ocirc:212 Otilde:213 Ouml:214 times:215 Oslash:216 Ugrave:217 Uacute:218 Ucirc:219 Uuml:220 Yacute:221 THORN:222 szlig:223 agrave:224 aacute:225 acirc:226 atilde:227 auml:228 aring:229 aelig:230 ccedil:231 egrave:232 eacute:233 ecirc:234 euml:235 igrave:236 iacute:237 icirc:238 iuml:239 eth:240 ntilde:241 ograve:242 oacute:243 ocirc:244 otilde:245 ouml:246 divide:247 oslash:248 ugrave:249 uacute:250 ucirc:251 uuml:252 yacute:253 thorn:254 yuml:255 OElig:338 oelig:339 Scaron:352 scaron:353 Yuml:376 fnof:402 circ:710 tilde:732 Alpha:913 Beta:914 Gamma:915 Delta:916 Epsilon:917 Zeta:918 Eta:919 Theta:920 Iota:921 Kappa:922 Lambda:923 Mu:924 Nu:925 Xi:926 Omicron:927 Pi:928 Rho:929 Sigma:931 Tau:932 Upsilon:933 Phi:934 Chi:935 Psi:936 Omega:937 alpha:945 beta:946 gamma:947 delta:948 epsilon:949 zeta:950 eta:951 theta:952 iota:953 kappa:954 lambda:955 mu:956 nu:957 xi:958 omicron:959 pi:960 rho:961 sigmaf:962 sigma:963 tau:964 upsilon:965 phi:966 chi:967 psi:968 omega:969 thetasym:977 upsih:978 piv:982 ensp:8194 emsp:8195 thinsp:8201 zwnj:8204 zwj:8205 lrm:8206 rlm:8207 ndash:8211 mdash:8212 lsquo:8216 rsquo:8217 sbquo:8218 ldquo:8220 rdquo:8221 bdquo:8222 dagger:8224 Dagger:8225 bull:8226 hellip:8230 permil:8240 prime:8242 Prime:8243 lsaquo:8249 rsaquo:8250 oline:8254 frasl:8260 euro:8364 image:8465 weierp:8472 real:8476 trade:8482 alefsym:8501 larr:8592 uarr:8593 rarr:8594 darr:8595 harr:8596 crarr:8629 lArr:8656 uArr:8657 rArr:8658 dArr:8659 hArr:8660 forall:8704 part:8706 exist:8707 empty:8709 nabla:8711 isin:8712 notin:8713 ni:8715 prod:8719 sum:8721 minus:8722 lowast:8727 radic:8730 prop:8733 infin:8734 ang:8736 and:8743 or:8744 cap:8745 cup:8746 int:8747 there4:8756 sim:8764 cong:8773 asymp:8776 ne:8800 equiv:8801 le:8804 ge:8805 sub:8834 sup:8835 nsub:8836 sube:8838 supe:8839 oplus:8853 otimes:8855 perp:8869 sdot:8901 lceil:8968 rceil:8969 lfloor:8970 rfloor:8971 loz:9674 spades:9824 clubs:9827 hearts:9829 diams:9830 lang:10216 rang:10217";

const NAMED_ENTITIES = new Map<string, string>(
  ENTITY_SOURCE.split(" ").map((pair) => {
    const [name, code] = pair.split(":");
    return [name, String.fromCodePoint(Number(code))];
  })
);

const REPLACEMENT_CHARACTER = "�";
const MAX_CODE_POINT = 0x10_ff_ff;
const SURROGATE_START = 0xd8_00;
const SURROGATE_END = 0xdf_ff;

function fromNumericReference(digits: string, radix: number): string {
  const code = Number.parseInt(digits, radix);
  if (
    code === 0 ||
    code > MAX_CODE_POINT ||
    (code >= SURROGATE_START && code <= SURROGATE_END)
  ) {
    return REPLACEMENT_CHARACTER;
  }
  return String.fromCodePoint(code);
}

const HEX_REFERENCE = /^&#[xX]([0-9a-fA-F]{1,6});$/;
const DECIMAL_REFERENCE = /^&#([0-9]{1,7});$/;
const NAMED_REFERENCE = /^&([A-Za-z][A-Za-z0-9]{1,31});$/;

/**
 * The character a reference such as `&eacute;`, `&#233;` or `&#xE9;` stands for, or
 * `null` when it is not one this module knows.
 */
export function decodeReference(reference: string): string | null {
  const hex = HEX_REFERENCE.exec(reference);
  if (hex) {
    return fromNumericReference(hex[1], 16);
  }
  const decimal = DECIMAL_REFERENCE.exec(reference);
  if (decimal) {
    return fromNumericReference(decimal[1], 10);
  }
  const named = NAMED_REFERENCE.exec(reference);
  if (named) {
    return NAMED_ENTITIES.get(named[1]) ?? null;
  }
  return null;
}

/** A character reference at the start of the subject. */
export const REFERENCE_AT_START =
  /^&(?:#[xX][0-9a-fA-F]{1,6}|#[0-9]{1,7}|[A-Za-z][A-Za-z0-9]{1,31});/;

const ESCAPE_OR_REFERENCE =
  /\\[!"#$%&'()*+,./:;<=>?@[\\\]^_`{|}~-]|&(?:#[xX][0-9a-fA-F]{1,6}|#[0-9]{1,7}|[A-Za-z][A-Za-z0-9]{1,31});/g;

/** True for the 32 ASCII punctuation characters a backslash can escape. */
const ASCII_PUNCTUATION = /^[!"#$%&'()*+,./:;<=>?@[\\\]^_`{|}~-]$/;

export function isAsciiPunctuation(ch: string): boolean {
  return ASCII_PUNCTUATION.test(ch);
}

/**
 * Resolve backslash escapes and character references, as CommonMark does inside link
 * destinations, titles and code-fence info strings. An unknown reference stays as typed.
 */
export function unescapeString(text: string): string {
  return text.replace(ESCAPE_OR_REFERENCE, (match) => {
    if (match.startsWith("\\")) {
      return match.slice(1);
    }
    return decodeReference(match) ?? match;
  });
}

const URI_SAFE = /^[A-Za-z0-9;/?:@&=+$,\-_.!~*'()#]$/;
const PERCENT_ESCAPE = /^%[0-9A-Fa-f]{2}$/;

/**
 * Percent-encode what a URL may not contain literally (spaces, non-ASCII, brackets),
 * leaving existing `%XX` escapes alone — the normalisation CommonMark's reference
 * renderer applies to every link destination.
 */
export function normalizeUri(uri: string): string {
  let out = "";
  let index = 0;
  while (index < uri.length) {
    const ch = uri[index];
    if (ch === "%" && PERCENT_ESCAPE.test(uri.slice(index, index + 3))) {
      out += uri.slice(index, index + 3);
      index += 3;
      continue;
    }
    if (URI_SAFE.test(ch)) {
      out += ch;
      index += 1;
      continue;
    }
    const code = uri.codePointAt(index) ?? 0;
    const whole = String.fromCodePoint(code);
    const lone = code >= SURROGATE_START && code <= SURROGATE_END;
    out += lone ? "%EF%BF%BD" : encodeURIComponent(whole);
    index += whole.length;
  }
  return out;
}

const HAS_SCHEME = /^[A-Za-z][A-Za-z0-9+.-]*:/;
const SAFE_SCHEME = /^(?:https?|mailto):/i;
const LAST_ASCII_CONTROL = 0x20;
const DELETE = 0x7f;

/**
 * True for an http(s) or mailto URL, or one with no scheme at all (a relative path or
 * a fragment).
 *
 * Browsers ignore leading control characters and spaces and drop tabs and newlines
 * anywhere in a URL, so `java\tscript:` is `javascript:` to them. Every character at or
 * below U+0020 is removed before the scheme is read — which can only make the check
 * stricter, never looser.
 */
export function isSafeUrl(url: string): boolean {
  let compact = "";
  for (const ch of url) {
    const code = ch.codePointAt(0) ?? 0;
    if (code > LAST_ASCII_CONTROL && code !== DELETE) {
      compact += ch;
    }
  }
  if (!HAS_SCHEME.test(compact)) {
    return true;
  }
  return SAFE_SCHEME.test(compact);
}

const LABEL_WHITESPACE = /[ \t\r\n]+/g;

/** A link label as CommonMark compares it: brackets off, whitespace collapsed, case folded. */
export function normalizeLabel(label: string): string {
  return label
    .slice(1, -1)
    .trim()
    .replace(LABEL_WHITESPACE, " ")
    .toLowerCase()
    .toUpperCase();
}

/*
 * CommonMark 0.31: a Unicode punctuation character is anything in the P (punctuation)
 * or S (symbol) categories.
 */
const UNICODE_PUNCTUATION = /^[\p{P}\p{S}]$/u;
const UNICODE_WHITESPACE = /^[\s\p{Zs}]$/u;

/** Whitespace for flanking purposes; the start or end of the text counts as whitespace. */
export function isUnicodeWhitespace(ch: string): boolean {
  return ch === "" || UNICODE_WHITESPACE.test(ch);
}

export function isUnicodePunctuation(ch: string): boolean {
  return ch !== "" && UNICODE_PUNCTUATION.test(ch);
}

const HIGH_SURROGATE_START = 0xd8_00;
const HIGH_SURROGATE_END = 0xdb_ff;
const LOW_SURROGATE_START = 0xdc_00;
const LOW_SURROGATE_END = 0xdf_ff;

/** The whole character (one or two UTF-16 units) ending just before `index`. */
export function characterBefore(text: string, index: number): string {
  if (index <= 0) {
    return "";
  }
  const low = text.charCodeAt(index - 1);
  if (low >= LOW_SURROGATE_START && low <= LOW_SURROGATE_END && index >= 2) {
    const high = text.charCodeAt(index - 2);
    if (high >= HIGH_SURROGATE_START && high <= HIGH_SURROGATE_END) {
      return text.slice(index - 2, index);
    }
  }
  return text[index - 1];
}

/** The whole character starting at `index`, or "" at the end. */
export function characterAt(text: string, index: number): string {
  if (index >= text.length) {
    return "";
  }
  const code = text.codePointAt(index) ?? 0;
  return String.fromCodePoint(code);
}
